import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import { useLocation } from "react-router-dom";
import UploadDesignModal from "./UploadDesignModal";
import "../../assets/css/OrderDetailsPage.css";

const normalizeText = (text) => {
  return text
    .toLocaleLowerCase("en-US")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};

const getColorValue = (options) => {
  if (!Array.isArray(options)) return "";
  for (let opt of options) {
    const name = normalizeText(opt.name);
    if (name.includes("color") || name.includes("renk")) {
      return opt.value?.trim() || "-";
    }
  }
  return "-";
};

const getOptionValue = (options, keywords) => {
  if (!Array.isArray(options)) return "";
  const normalized = (text) =>
    text
      .toLocaleLowerCase("en-US")
      .replace(/ı/g, "i")
      .replace(/İ/g, "i")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  const match = options.find((opt) =>
    keywords.some((keyword) => normalized(opt.name) === normalized(keyword)),
  );
  return match?.value || "-";
};

const getSizeValue = (options) => {
  if (!Array.isArray(options)) return "";
  for (let opt of options) {
    const name = normalizeText(opt.name);
    const value = opt.value?.trim() || "";
    if (
      name.includes("size") ||
      name.includes("sıze") ||
      name.includes("beden") ||
      value.includes(" - ") // örn: "Sweatshirt - L"
    ) {
      return value;
    }
  }
  return "-";
};

const isSizeOrColorOption = (optionName = "") => {
  const name = normalizeText(optionName);
  return (
    name.includes("size") ||
    name.includes("sıze") ||
    name.includes("beden") ||
    name.includes("color") ||
    name.includes("renk")
  );
};

const getCustomOptions = (options = []) => {
  if (!Array.isArray(options)) return [];

  return options.filter((opt) => {
    if (!opt || !opt.name) return false;
    if (isSizeOrColorOption(opt.name)) return false;
    return String(opt.value ?? "").trim() !== "";
  });
};

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const ordersList = location.state?.orders || [];
  const userEmail = localStorage.getItem("email");
  const isEmbroideryUser = userEmail === "embroidery@txmaya.com";
  const [showUploadModal, setShowUploadModal] = useState(false);
  const currentIndex = ordersList.findIndex(
    (o) => String(o.orderId) === String(orderId),
  );

  const prevOrderId =
    currentIndex > 0 ? ordersList[currentIndex - 1]?.orderId : null;
  const nextOrderId =
    currentIndex >= 0 && currentIndex < ordersList.length - 1
      ? ordersList[currentIndex + 1]?.orderId
      : null;

  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editNotes, setEditNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);
  const [editOptionState, setEditOptionState] = useState({});
  const [editingAddress, setEditingAddress] = useState(false);
  const [shipToDraft, setShipToDraft] = useState({});
  const [tags, setTags] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // Yeni state: copy butonunun aktif olup olmadığını kontrol eder
  const [isCopyEnabled, setIsCopyEnabled] = useState(false);
  // Yeni state: tag loading durumu
  const [tagLoading, setTagLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${settings.apiURL}/get_shipstation_tags.php`, {
        headers: authHeader(),
      })
      .then((res) => {
        setTags(res.data.tags || []);
      })
      .catch((err) => {
        setTags([]);
      });
  }, []);

  const fetchOrderDetails = () => {
    setLoading(true);
    setIsCopyEnabled(false); // Yeni order yüklenirken copy butonunu devre dışı bırak
    axios
      .get(
        `${settings.apiURL}/get_order_details_from_db.php?orderId=${orderId}`,
        { headers: authHeader() },
      )
      .then((res) => {
        setOrder(res.data.order);
        setEditNotes({
          internalNote: res.data.order.internalNotes || "",
          customerNotes: res.data.order.customerNotes || "",
        });
        setLoading(false);
        // Order tam yüklendiğinde copy butonunu aktif et
        setTimeout(() => {
          setIsCopyEnabled(true);
        }, 100);
      })
      .catch((err) => {
        setLoading(false);
        setIsCopyEnabled(false); // Hata durumunda da devre dışı bırak
      });
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  useEffect(() => {
    setEditOptionState({});
    setNotesChanged(false);
    setShipToDraft({});
    setIsCopyEnabled(false); // Order değiştiğinde copy butonunu devre dışı bırak
  }, [orderId]);

  // Kopyalama fonksiyonu - 0 AMZ için özel format
  const handleCopy = (item) => {
    if (!isCopyEnabled || !order) {
      toast("Please wait for order to load completely!", "warning");
      return;
    }

    const shopName = order.shopName || "Shop Name";
    const customerName = order.shipTo?.name || "Customer";
    const orderNumber = `#${order.orderNumber}`;
    const firstName = localStorage.getItem("first_name") || "";
    const lastName = localStorage.getItem("last_name") || "";
    const userName = `${firstName} ${lastName}`.trim() || "User";

    const allOptions = item.options || [];
    const size = getSizeValue(allOptions);
    const color = getColorValue(allOptions);
    const customOptions = getCustomOptions(item.options);

    let textToCopy = "";

    // 0 AMZ mağazası için özel format
    if (shopName === "0 AMZ" || shopName === "0 VILLA") {
      // SKU'dan RET önekini kaldır
      let cleanedSku = item.sku || "";
      if (cleanedSku.startsWith("RET")) {
        cleanedSku = cleanedSku.substring(3).trim();
      }

      // Tireleri temizle ve parçalara ayır
      const parts = [size, color, "Gildan Heavy Cotton Tshirt", cleanedSku]
        .filter((part) => part && part !== "-" && part.trim() !== "")
        .map((part) => part.trim());

      textToCopy = `${shopName} - ${orderNumber} - ${customerName} - ${userName}
${parts.join(" - ")} - quantity: ${item.quantity}`;

      customOptions.forEach((opt) => {
        textToCopy += `\n${opt.name}: ${opt.value}`;
      });
    } else {
      // Diğer mağazalar için normal format
      textToCopy = `${shopName} - ${orderNumber} - ${customerName} - ${userName}
${size} - ${color} - quantity: ${item.quantity}`;

      customOptions.forEach((opt) => {
        textToCopy += `\n${opt.name}: ${opt.value}`;
      });
    }

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => toast("Copied to clipboard!", "success"))
      .catch(() => toast("Copy failed!", "error"));
  };

  const handleCopyBarcode = async () => {
    if (!isCopyEnabled || !order) {
      toast("Please wait for order to load completely!", "warning");
      return;
    }

    const orderNumber = order.orderNumber;
    if (!orderNumber) {
      toast("Order number not found!", "error");
      return;
    }

    const barcodeText = `*${orderNumber}*`;
    const barcodeFont = '50px "Libre Barcode 39"';
    const labelFont = "12px Arial";
    const padding = 4; // kenar boşluğu
    const spacing = 2; // barkod ile yazı arası

    try {
      await document.fonts.load(barcodeFont);
      await document.fonts.ready;

      // Ölçüm için 1x ölçekli canvas
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");
      tempCtx.font = barcodeFont;
      const barcodeMetrics = tempCtx.measureText(barcodeText);
      tempCtx.font = labelFont;
      const labelMetrics = tempCtx.measureText(orderNumber);

      const barcodeHeight = Math.ceil(
        barcodeMetrics.actualBoundingBoxAscent +
          barcodeMetrics.actualBoundingBoxDescent,
      );
      const labelHeight = Math.ceil(
        labelMetrics.actualBoundingBoxAscent +
          labelMetrics.actualBoundingBoxDescent,
      );

      const contentWidth = Math.ceil(
        Math.max(barcodeMetrics.width, labelMetrics.width),
      );
      const contentHeight = barcodeHeight + spacing + labelHeight;

      const width = contentWidth + padding * 2;
      const height = contentHeight + padding * 2;

      // Illustrator için tam 3.5 inç genişlik
      const scale = 336 / width;

      // Yüksek çözünürlüklü canvas
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");

      // Ölçekle
      ctx.scale(scale, scale);

      // Arka plan
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, width, height);

      // Barkod
      ctx.font = barcodeFont;
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(barcodeText, width / 2, padding);

      // Alt yazı (yakın ama temas etmiyor)
      ctx.font = labelFont;
      ctx.textBaseline = "top";
      ctx.fillText(orderNumber, width / 2, padding + barcodeHeight + spacing);

      // Görseli kopyala
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              "image/png": blob,
            }),
          ]);
          toast("Barcode copied at exactly 3.5 inch width!", "success");
        } catch (err) {
          await navigator.clipboard.writeText(barcodeText);
          toast(
            "Fallback: barcode text copied. Use Libre Barcode 39 font.",
            "success",
          );
        }
      });
    } catch (err) {
      try {
        await navigator.clipboard.writeText(barcodeText);
        toast(
          "Fallback: barcode text copied. Use Libre Barcode 39 font.",
          "success",
        );
      } catch (fallbackErr) {
        toast("Copy failed!", "error");
      }
    }
  };

  const handleNoteChange = (field, value) => {
    setEditNotes((prev) => ({ ...prev, [field]: value }));
    setNotesChanged(true);
  };

  const startEditingAddress = () => {
    setShipToDraft(order.shipTo || {});
    setEditingAddress(true);
  };

  const cancelEditingAddress = () => {
    setEditingAddress(false);
  };

  const handleAddressChange = (field, value) => {
    setShipToDraft((prev) => ({ ...prev, [field]: value }));
  };

  const saveAddress = () => {
    axios
      .post(
        `${settings.apiURL}/update_shipping_address.php`,
        { orderId: order.orderId, shipTo: shipToDraft },
        { headers: authHeader() },
      )
      .then((res) => {
        if (res.data.success) {
          toast("Address updated!", "success");
          setOrder((prev) => ({ ...prev, shipTo: shipToDraft }));
          setEditingAddress(false);
        } else {
          toast("Failed to update address", "error");
        }
      })
      .catch(() => {
        toast("Error updating address", "error");
      });
  };

  const saveNotes = () => {
    if (!notesChanged) return;
    setSaving(true);
    axios
      .post(
        `${settings.apiURL}/update_order_notes.php`,
        { orderId, ...editNotes },
        { headers: authHeader() },
      )
      .then(() => {
        setSaving(false);
        setNotesChanged(false);
        toast("Notes updated successfully!", "success");
      })
      .catch(() => {
        setSaving(false);
        toast("Failed to update notes!", "error");
      });
  };

  const handleSaveOption = (
    orderId,
    lineItemKey,
    optionName,
    newValue,
    key,
  ) => {
    axios
      .post(
        `${settings.apiURL}/update_item_options.php`,
        {
          orderId,
          lineItemKey,
          optionName,
          newValue,
        },
        { headers: authHeader() },
      )
      .then((res) => {
        if (res.data.success) {
          toast("Option updated successfully!", "success");
          setEditOptionState((prev) => ({
            ...prev,
            [key]: { editing: false, value: newValue },
          }));

          // ✅ Frontend'de anında güncelle
          setOrder((prevOrder) => {
            const updatedItems = prevOrder.items.map((item) => {
              if (item.lineItemKey === lineItemKey) {
                const newOptions = item.options.map((opt) => {
                  if (opt.name.toLowerCase() === optionName.toLowerCase()) {
                    return { ...opt, value: newValue };
                  }
                  return opt;
                });
                return { ...item, options: newOptions };
              }
              return item;
            });
            return { ...prevOrder, items: updatedItems };
          });
        } else {
          toast(res.data.message || "Update failed!", "error");
        }
      })
      .catch((err) => {
        toast("Update failed!", "error");
      });
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-US") : "-";

  if (loading) return <p>Loading...</p>;
  if (!order) return <p>Order not found.</p>;

  const itemsFiltered =
    order.items?.filter(
      (item) => !["discount", "shipping"].includes(item.name.toLowerCase()),
    ) || [];
  const discountItem = order.items?.find(
    (item) => item.name.toLowerCase() === "discount",
  );
  const discountAmount = discountItem
    ? -Math.abs(discountItem.unitPrice * discountItem.quantity)
    : 0;

  const sectionStyle = {
    border: "1px solid #ccc",
    padding: "15px",
    marginBottom: "15px",
    borderRadius: "5px",
  };
  const tableStyle = {
    width: "100%",
    fontSize: "13px",
    borderCollapse: "collapse",
  };
  const cellStyle = { border: "1px solid #ccc", padding: "8px" };
  const thumbnailStyle = {
    width: "50px",
    transition: "transform 0.3s ease",
    cursor: "pointer",
  };

  const createTrackingUrl = (tracking) => {
    if (tracking.trackingUrl) return tracking.trackingUrl;

    const trackingNumber = tracking.trackingNumber;
    const carrier = tracking.carrierCode?.toLowerCase() || "";

    // UPS için (ups_walleted, ups_ground, ups_2nd_day_air, ups_next_day_air, vs)
    if (carrier.includes("ups")) {
      return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    }

    // USPS için (usps, usps_first_class_mail, usps_priority_mail, vs)
    if (carrier.includes("usps") || carrier.includes("stamps_com")) {
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    }

    // FedEx için
    if (carrier.includes("fedex")) {
      return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    }

    // DHL için
    if (carrier.includes("dhl")) {
      return `https://www.dhl.com/us-en/home/tracking/tracking-parcel.html?submit=1&tracking-id=${trackingNumber}`;
    }

    // Bilinmeyen carrier için Google arama
    return `https://www.google.com/search?q=${encodeURIComponent(
      trackingNumber + " tracking",
    )}`;
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "20px",
        padding: "20px",
        fontSize: "14px",
        position: "relative",
      }}
    >
      <button
        onClick={() =>
          navigate(isEmbroideryUser ? "/embroidery-my-orders" : "/my-orders")
        }
        style={{
          position: "absolute",
          top: "10px",
          right: "20px",
          fontSize: "20px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
        }}
      >
        ❌
      </button>

      <aside style={{ flex: "0 0 300px" }}>
        <h4>Order Summary</h4>
        <p>
          <strong>Order #</strong>
          <br />
          {order.orderNumber}
        </p>
        <p>
          <strong>Status</strong>
          <br />
          {order.orderStatus || "-"}
        </p>
        <p>
          <strong>Sold To</strong>
          <br />
          {order.shipTo?.name}
          <br />
          {order.customerEmail || "-"}
        </p>
        <p>
          <strong>Ship To Address</strong>
        </p>

        {editingAddress ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <input
              value={shipToDraft.name || ""}
              onChange={(e) => handleAddressChange("name", e.target.value)}
              placeholder="Name"
            />
            <input
              value={shipToDraft.street1 || ""}
              onChange={(e) => handleAddressChange("street1", e.target.value)}
              placeholder="Street"
            />
            <input
              value={shipToDraft.city || ""}
              onChange={(e) => handleAddressChange("city", e.target.value)}
              placeholder="City"
            />
            <input
              value={shipToDraft.state || ""}
              onChange={(e) => handleAddressChange("state", e.target.value)}
              placeholder="State"
            />
            <input
              value={shipToDraft.postalCode || ""}
              onChange={(e) =>
                handleAddressChange("postalCode", e.target.value)
              }
              placeholder="Postal Code"
            />
            <input
              value={shipToDraft.country || ""}
              onChange={(e) => handleAddressChange("country", e.target.value)}
              placeholder="Country"
            />
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button onClick={saveAddress} className="btn btn-success btn-sm">
                Save
              </button>
              <button
                onClick={cancelEditingAddress}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {order.shipTo?.name}
            <br />
            {order.shipTo?.street1}
            <br />
            {order.shipTo?.city}, {order.shipTo?.state}{" "}
            {order.shipTo?.postalCode}
            <br />
            {order.shipTo?.country}
            <br />
            <button
              onClick={startEditingAddress}
              className="btn btn-link p-0 mt-1"
            >
              Edit Address
            </button>
          </div>
        )}

        <p>
          <strong>Product:</strong> $
          {itemsFiltered
            .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
            .toFixed(2)}
        </p>
        <p>
          <strong>Shipping:</strong> ${order.shippingAmount}
        </p>
        <p>
          <strong>Tax:</strong> ${order.taxAmount}
        </p>
        <p>
          <strong>Order Total:</strong> ${order.orderTotal}
        </p>
        <p>
          <strong>Discount Total:</strong>{" "}
          {discountAmount !== 0 ? `$${discountAmount.toFixed(2)}` : "-$0.00"}
        </p>
        <p>
          <strong>Total Paid:</strong> ${order.amountPaid}
        </p>
        <p>
          <strong>Order Date:</strong> {formatDate(order.createDate)}
        </p>
        <p>
          <strong>Ship By:</strong> {formatDate(order.shipByDate)}
        </p>
        <p>
          <strong>Order Items:</strong> {itemsFiltered.length} items
        </p>
      </aside>

      <main style={{ flex: 1 }}>
        <h3>Shipment Details</h3>
        <div
          style={{
            ...sectionStyle,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "20px",
          }}
        >
          {/* Left: Ship To Address */}
          <div>
            <p>
              <strong>Ship To Address</strong>
              <br />
              {order.shipTo?.name}
              <br />
              {order.shipTo?.street1}
              <br />
              {order.shipTo?.city}, {order.shipTo?.state}{" "}
              {order.shipTo?.postalCode}
              <br />
              {order.shipTo?.country}
            </p>
            <span
              style={{
                backgroundColor:
                  order.shipTo?.addressVerified ===
                  "Address validated successfully"
                    ? "#d4edda"
                    : "#f8d7da",
                color:
                  order.shipTo?.addressVerified ===
                  "Address validated successfully"
                    ? "#155724"
                    : "#721c24",
                padding: "4px 8px",
                borderRadius: "6px",
                display: "inline-block",
                marginTop: "5px",
                fontWeight: "bold",
              }}
            >
              {order.shipTo?.addressVerified ===
              "Address validated successfully"
                ? "✅ Address Validated"
                : "⚠️ Address Not Validated"}
            </span>
          </div>

          {/* Right: Tag Dropdown */}
          <div
            style={{
              ...sectionStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            {/* Sol: Tracking Information */}
            <div style={{ flex: 1 }}>
              <h4>Tracking Information</h4>
              {order.trackingInfo && order.trackingInfo.length > 0 ? (
                order.trackingInfo.map((tracking, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "10px",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <strong>Tracking #{index + 1}:</strong>
                    <br />
                    <strong>Number:</strong> {tracking.trackingNumber}
                    <br />
                    <strong>Link:</strong>{" "}
                    <a
                      href={createTrackingUrl(tracking)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#007bff",
                        textDecoration: "underline",
                      }}
                    >
                      📦 Track Package ({tracking.carrierCode || "Unknown"})
                    </a>
                    <br />
                    <strong>Carrier:</strong>{" "}
                    {tracking.carrierCode || "Unknown"}
                    <br />
                    {tracking.shipDate && (
                      <>
                        <strong>Ship Date:</strong>{" "}
                        {formatDate(tracking.shipDate)}
                      </>
                    )}
                  </div>
                ))
              ) : (
                <p>No tracking information available</p>
              )}
            </div>

            {/* Sağ: Tag Dropdown */}
          </div>
          <div style={{ minWidth: "180px", position: "relative" }}>
            <label style={{ fontWeight: "bold" }}>Tag:</label>
            <select
              className="form-select form-select-sm"
              value={order.tagIds?.[0] || ""}
              disabled={tagLoading}
              onChange={(e) => {
                const selectedTagId = e.target.value;
                setTagLoading(true);

                const payload =
                  selectedTagId === ""
                    ? {
                        orderNumber: order.orderNumber,
                        orderId: order.orderId,
                        tagId: "remove",
                        removeTagId: order.tagIds?.[0],
                      }
                    : {
                        orderNumber: order.orderNumber,
                        orderId: order.orderId,
                        tagId: selectedTagId,
                      };

                axios
                  .post(`${settings.apiURL}/update_order_tag.php`, payload, {
                    headers: authHeader(),
                  })
                  .then((res) => {
                    if (res.data.success) {
                      toast(res.data.message, "success");
                      setOrder((prevOrder) => ({
                        ...prevOrder,
                        tagIds: selectedTagId === "" ? [] : [selectedTagId],
                      }));
                    } else {
                      toast("Failed to update tag!", "error");
                    }
                  })
                  .catch((err) => {
                    toast("Tag update failed!", "error");
                  })
                  .finally(() => {
                    setTagLoading(false);
                  });
              }}
            >
              <option value="">No tag</option>
              {tags.map((tag) => (
                <option key={tag.tagId} value={tag.tagId}>
                  {tag.name}
                </option>
              ))}
            </select>
            {tagLoading && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "30px",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #f3f3f3",
                    borderTop: "2px solid #007bff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
              </div>
            )}
          </div>
        </div>

        <div style={sectionStyle}>
          <h4>Shipment Items</h4>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={cellStyle}>Image</th>
                <th style={cellStyle}>Item Name</th>
                <th style={cellStyle}>SKU</th>
                <th style={cellStyle}>Unit Cost</th>
                <th style={cellStyle}>Quantity</th>
                <th style={cellStyle}>Info</th>
              </tr>
            </thead>
            <tbody>
              {itemsFiltered.map((item, i) => {
                const isDTFOrder = !item.options?.some((opt) => {
                  const name = normalizeText(opt.name);
                  return name.includes("size") || name.includes("color");
                });

                return (
                  <tr key={i}>
                    <td style={cellStyle}>
                      <img
                        src={item.imageUrl}
                        key={item.lineItemKey}
                        alt=""
                        style={thumbnailStyle}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.transform = "scale(5)")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                      />
                    </td>
                    <td style={cellStyle}>
                      {item.name}
                      <br />
                      {item.options
                        ?.filter((opt) => {
                          return isSizeOrColorOption(opt.name);
                        })
                        .map((opt, j) => {
                          const key = `${i}-${j}`;
                          const isEditing =
                            editOptionState[key]?.editing || false;
                          const tempValue =
                            editOptionState[key]?.value ?? opt.value;

                          return (
                            <div key={j} style={{ marginTop: "5px" }}>
                              <strong>{opt.name}:</strong>{" "}
                              {isEditing ? (
                                <>
                                  <input
                                    type="text"
                                    value={tempValue}
                                    onChange={(e) =>
                                      setEditOptionState((prev) => ({
                                        ...prev,
                                        [key]: {
                                          ...prev[key],
                                          value: e.target.value,
                                        },
                                      }))
                                    }
                                    style={{ width: "150px", fontSize: "12px" }}
                                  />
                                  <button
                                    style={{
                                      marginLeft: "5px",
                                      fontSize: "12px",
                                    }}
                                    onClick={() =>
                                      handleSaveOption(
                                        order.orderId,
                                        item.lineItemKey,
                                        opt.name,
                                        tempValue,
                                        key,
                                      )
                                    }
                                  >
                                    Save
                                  </button>
                                  <button
                                    style={{
                                      marginLeft: "5px",
                                      fontSize: "12px",
                                    }}
                                    onClick={() =>
                                      setEditOptionState((prev) => ({
                                        ...prev,
                                        [key]: {
                                          ...prev[key],
                                          editing: false,
                                          value: opt.value, // Eski değeri geri getiriyoruz
                                        },
                                      }))
                                    }
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  {opt.value}
                                  <span
                                    style={{
                                      color: "blue",
                                      cursor: "pointer",
                                      marginLeft: "5px",
                                      fontSize: "12px",
                                    }}
                                    onClick={() =>
                                      setEditOptionState((prev) => ({
                                        ...prev,
                                        [key]: {
                                          editing: true,
                                          value: opt.value,
                                        },
                                      }))
                                    }
                                  >
                                    Edit
                                  </span>
                                </>
                              )}
                            </div>
                          );
                        })}

                      <div style={{ marginTop: "8px" }}>
                        {getCustomOptions(item.options).length > 0 ? (
                          getCustomOptions(item.options).map((opt, idx) => (
                            <div key={`custom-opt-${i}-${idx}`}>
                              <strong>{opt.name}:</strong> {opt.value}
                            </div>
                          ))
                        ) : (
                          <div>
                            <strong>Custom Options:</strong> Not provided on this
                            item.
                          </div>
                        )}
                        {order.shopName === "0 AMZ" && (
                          <div style={{ marginTop: "5px" }}>
                            <strong>Amazon Order Link:</strong>{" "}
                            <a
                              href={`https://sellercentral.amazon.com/orders-v3/order/${order.orderNumber}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "blue",
                                textDecoration: "underline",
                              }}
                            >
                              View Order
                            </a>
                          </div>
                        )}
                        {(() => {
                          const targetNames = [
                            "dimension",
                            "dimensions",
                            "style",
                            "size",
                            "beden",
                          ];
                          const dimensionOption = item.options?.find((opt) =>
                            targetNames.includes(normalizeText(opt.name)),
                          );
                          return dimensionOption ? (
                            <div style={{ marginTop: "5px" }}>
                              <strong>Dimensions:</strong>{" "}
                              {dimensionOption.value}
                            </div>
                          ) : null;
                        })()}
                      </div>

                      <button
                        onClick={() => {
                          setSelectedItem(item);
                          setShowUploadModal(true);
                        }}
                        style={{
                          backgroundColor: "#f59e0b",
                          color: "white",
                          padding: "8px 16px",
                          border: "none",
                          borderRadius: "6px",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          fontWeight: "bold",
                          cursor: "pointer",
                          marginTop: "8px",
                        }}
                      >
                        Upload Designs
                      </button>
                      {/* 🔄 Design Status alanı - item bazında kontrol */}
                      {item.design_uploaded && (
                        <div
                          style={{
                            backgroundColor: "#dcfce7", // Açık yeşil zemin
                            color: "#16a34a", // Yeşil yazı
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontWeight: "bold",
                            fontSize: "13px",
                            marginBottom: "8px",
                            display: "inline-block",
                          }}
                        >
                          ✅ Uploaded
                        </div>
                      )}
                    </td>

                    <td style={cellStyle}>{item.sku}</td>
                    <td style={cellStyle}>${item.unitPrice}</td>
                    <td style={cellStyle}>{item.quantity}</td>
                    <td style={cellStyle}>
                      <button
                        onClick={() => handleCopy(item)}
                        disabled={!isCopyEnabled}
                        style={{
                          backgroundColor: isCopyEnabled
                            ? "#007bff"
                            : "#6c757d",
                          color: "white",
                          border: "none",
                          padding: "5px 10px",
                          borderRadius: "4px",
                          cursor: isCopyEnabled ? "pointer" : "not-allowed",
                        }}
                      >
                        Copy
                      </button>

                      <button
                        onClick={handleCopyBarcode}
                        disabled={!isCopyEnabled}
                        style={{
                          backgroundColor: isCopyEnabled
                            ? "#28a745"
                            : "#6c757d",
                          color: "white",
                          border: "none",
                          padding: "5px 10px",
                          borderRadius: "4px",
                          cursor: isCopyEnabled ? "pointer" : "not-allowed",
                          display: "block",
                          width: "100%",
                          marginTop: "5px",
                        }}
                      >
                        Copy Barcode
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={sectionStyle}>
          <h4>Notes</h4>
          <label>Internal Note:</label>
          <textarea
            value={
              editNotes.internalNote === "No notes available"
                ? ""
                : editNotes.internalNote
            }
            onChange={(e) => handleNoteChange("internalNote", e.target.value)}
            style={{ width: "100%", minHeight: "50px" }}
          />
          <label>Customer Note:</label>
          <textarea
            value={
              editNotes.customerNotes === "No notes available"
                ? ""
                : editNotes.customerNotes
            }
            onChange={(e) => handleNoteChange("customerNotes", e.target.value)}
            style={{ width: "100%", minHeight: "50px", marginTop: "10px" }}
          />

          {order.giftMessage && (
            <div
              style={{
                marginTop: "15px",
                padding: "10px",
                background: "#f9f9f9",
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            >
              <strong>Gift Note:</strong>
              <p>{order.giftMessage}</p>
            </div>
          )}

          <button
            className="btn btn-primary mt-2"
            onClick={saveNotes}
            disabled={saving}
          >
            Save Notes
          </button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            className="btn"
            style={{
              backgroundColor: prevOrderId ? "#FFA500" : "#ccc",
              color: "white",
              cursor: prevOrderId ? "pointer" : "not-allowed",
              pointerEvents: prevOrderId ? "auto" : "none",
            }}
            onClick={() =>
              navigate(`/order-details/${prevOrderId}`, {
                state: {
                  orders: ordersList,
                  from: isEmbroideryUser
                    ? "/embroidery-my-orders"
                    : "/my-orders",
                },
              })
            }
          >
            ⬅️ Previous
          </button>
          <button
            className="btn"
            style={{
              backgroundColor: nextOrderId ? "#FFA500" : "#ccc",
              color: "white",
              cursor: nextOrderId ? "pointer" : "not-allowed",
              pointerEvents: nextOrderId ? "auto" : "none",
            }}
            onClick={() =>
              navigate(`/order-details/${nextOrderId}`, {
                state: {
                  orders: ordersList,
                  from: isEmbroideryUser
                    ? "/embroidery-my-orders"
                    : "/my-orders",
                },
              })
            }
          >
            Next ➡️
          </button>
        </div>
      </main>
      <UploadDesignModal
        key={`upload-modal-${selectedItem?.lineItemKey || "default"}-${
          selectedItem?.orderItemId || "default"
        }`}
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setSelectedItem(null);
          fetchOrderDetails(); // 🔁 tasarım gönderildikten sonra güncelle
        }}
        order={order}
        item={selectedItem}
        itemSku={selectedItem?.sku} // 🆕 SKU bilgisini gönder
        copyMeta={{
          shopName: order?.storeName || "Shop",
          orderNumber: order?.orderNumber || "Unknown",
          customerName: order?.shipTo?.name || "Customer",
          userName: `${localStorage.getItem("first_name") || ""} ${
            localStorage.getItem("last_name") || "User"
          }`,
        }}
      />
      <style>
        {`
          @keyframes spin {
            0% { transform: translateY(-50%) rotate(0deg); }
            100% { transform: translateY(-50%) rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default OrderDetailsPage;
